/**
 * The **How to Play** surface (FS-01 R40–R42, FS-12 R34–R36).
 *
 * FS-01 R09 forbids tutorial text *during a run*; R40 narrows it — a reference a player chooses to
 * open is not a tutorial imposed on one. So this screen may use text and diagrams, and it is never
 * auto-modal, never blocking, and always dismissible with a single input.
 *
 * **Nothing here writes a mechanic sentence.** Every card is generated from `POWERUPS`
 * (`src/game/powerups.ts`), `HUD_ELEMENTS` and `HAZARD_FAMILIES` (`src/content/mechanics.ts`), and
 * every hazard illustration is drawn from the hazard kind's own `innerRadius` through the renderer's
 * `bandRadiiAt`. That is FS-12 R35: a power-up cannot be added without its explanation, and a card
 * cannot show a shape the game does not draw. `tests/how-to-play.test.ts` enforces both.
 */
import { POWERUPS, ALL_POWERUPS } from '../game/powerups';
import { HUD_ELEMENTS, HAZARD_FAMILIES } from '../content/mechanics';
import { HAZARD_KINDS } from '../game/hazards/kinds';
import { PLAY_RADIUS, TUNNEL_RADIUS } from '../core/constants';
import { bandRadiiAt, type BandRadii } from '../render/hazard-view';
import type { HazardKind } from '../core/event-map';
import type { HazardParams } from '../game/hazards/types';

const TAU = Math.PI * 2;
/** Enough segments for a legible 120 px illustration; the live renderer uses 96. */
const SVG_SEG = 72;
const SVG_SIZE = 120;

/**
 * A representative hazard at mid intensity, wide node, rim polarity. `scaleByIntensity` is the
 * kind's own function (FS-06 R28), so the illustration is the shape the chart actually produces at
 * Intensity 0.5 rather than an artist's impression of it.
 */
function representativeParams(kind: HazardKind): HazardParams {
  const def = HAZARD_KINDS[kind];
  const p: HazardParams = {
    amp: 0,
    freq: 2,
    phase: 0,
    nodeTheta: 0,
    nodeHalfTheta: 0.42,
    nodeRMin: 0,
    nodeRMax: PLAY_RADIUS,
    polarity: 'rim',
    reach: 1,
    count: 4,
    rate: 0,
    seed: 12345,
  };
  def.scaleByIntensity(0.5, p);
  return p;
}

const _band: BandRadii = { inner: 0, outer: 0 };

/**
 * The lethal band as two SVG rings with an even-odd fill: outer boundary clockwise, inner boundary
 * anticlockwise, so the gap in the middle — and the node — read as a hole rather than as fill.
 */
function silhouetteSvg(kind: HazardKind): string {
  const def = HAZARD_KINDS[kind];
  const p = representativeParams(kind);
  const c = SVG_SIZE / 2;
  const scale = c / TUNNEL_RADIUS;
  const outer: string[] = [];
  const inner: string[] = [];
  for (let i = 0; i <= SVG_SEG; i++) {
    const theta = (i / SVG_SEG) * TAU;
    bandRadiiAt(def, p, theta, 0, p.nodeTheta, _band);
    const sx = Math.sin(theta);
    const cy = Math.cos(theta);
    outer.push(`${(c + sx * _band.outer * scale).toFixed(2)},${(c - cy * _band.outer * scale).toFixed(2)}`);
    // Anticlockwise, so even-odd fill punches the inside out.
    inner.unshift(`${(c + sx * _band.inner * scale).toFixed(2)},${(c - cy * _band.inner * scale).toFixed(2)}`);
  }
  return `<svg class="htp-shape" viewBox="0 0 ${SVG_SIZE} ${SVG_SIZE}" aria-hidden="true">
    <circle class="htp-shape-wall" cx="${c}" cy="${c}" r="${(TUNNEL_RADIUS * scale).toFixed(2)}" />
    <path class="htp-shape-fill" fill-rule="evenodd" d="M${outer.join('L')}Z M${inner.join('L')}Z" />
    <circle class="htp-shape-ship" cx="${c}" cy="${c}" r="3" />
  </svg>`;
}

export type HowToPlaySection = 'hazards' | 'powerups' | 'hud';

export interface HowToPlayCard {
  readonly section: HowToPlaySection;
  /** The registry id this card was generated from. One card per id, enforced by the test. */
  readonly id: string;
  readonly title: string;
  /** Copied verbatim from the registry — never authored here (R35). */
  readonly sentence: string;
  readonly foot: string;
  /** Inline markup for the R42 image. */
  readonly image: string;
}

function cardHtml(c: HowToPlayCard): string {
  return `<div class="htp-card">
    <div class="htp-image">${c.image}</div>
    <div class="htp-body">
      <h3>${escapeHtml(c.title)}</h3>
      <p>${escapeHtml(c.sentence)}</p>
      ${c.foot ? `<p class="htp-foot">${escapeHtml(c.foot)}</p>` : ''}
    </div>
  </div>`;
}

/**
 * R42's "one image" for a HUD element is a miniature of the readout itself, built from the same
 * `hud.css` classes the live HUD uses — so restyling the HUD restyles the manual. Structure only;
 * every word on these cards comes from `HUD_ELEMENTS`.
 */
function hudImage(id: string): string {
  switch (id) {
    case 'score':
      return '<span class="htp-mini value">12,480</span>';
    case 'multiplier':
      return '<span class="htp-mini value">4×</span>';
    case 'speed-tier':
      return '<span class="htp-mini value">3</span>';
    case 'resonance':
      return '<span class="htp-mini meter"><span class="meter-track"><span class="meter-fill" style="width:62%"></span></span></span>';
    case 'overdrive':
      return '<span class="htp-mini meter"><span class="meter-track"><span class="meter-fill overdrive" style="width:100%"></span></span></span>';
    case 'powerup-stored':
      return `<span class="htp-glyph">${POWERUPS['Harmonic Shield'].glyph}</span>`;
    case 'beat':
      return '<span class="htp-mini pips"><i class="pip on downbeat"></i><i class="pip"></i><i class="pip"></i><i class="pip"></i></span>';
    case 'boss':
      return '<span class="htp-mini meter"><span class="boss-track"><span class="boss-fill" style="width:50%"></span></span></span>';
    default:
      return '<span class="htp-glyph small">·</span>';
  }
}

/** How a power-up spends itself, phrased from `spend`/`amount` rather than written per power-up. */
function spendLine(name: (typeof ALL_POWERUPS)[number]): string {
  const d = POWERUPS[name];
  if (d.spend === 'timed') return `${d.amount}s`;
  if (d.spend === 'charge') return `${d.amount} charges`;
  return 'One use';
}

/**
 * Every card on the screen, generated from the three registries and nothing else (FS-12 R35).
 * Exported separately from the renderer so `tests/how-to-play.test.ts` can assert the generation
 * rule without a DOM: adding a power-up, a built HUD element or a hazard family without its
 * explanation fails there, not in review.
 */
export function howToPlayCards(): HowToPlayCard[] {
  const cards: HowToPlayCard[] = [];
  for (const f of HAZARD_FAMILIES) {
    cards.push({
      section: 'hazards',
      id: f.id,
      title: f.displayName,
      sentence: f.oneSentence,
      foot: f.kinds.join(' · '),
      image: silhouetteSvg(f.kinds[0]!),
    });
  }
  for (const name of ALL_POWERUPS) {
    cards.push({
      section: 'powerups',
      id: name,
      title: name,
      sentence: POWERUPS[name].oneSentence,
      foot: spendLine(name),
      image: `<span class="htp-glyph">${POWERUPS[name].glyph}</span>`,
    });
  }
  for (const e of HUD_ELEMENTS) {
    if (!e.built) continue;
    cards.push({
      section: 'hud',
      id: e.id,
      title: e.displayName,
      sentence: e.oneSentence,
      foot: e.nonColourChannel,
      image: hudImage(e.id),
    });
  }
  return cards;
}

export interface HowToPlayOptions {
  /** Named for the device in the player's hands (FS-07 R37, FS-12 R40) — never `Space` on a phone. */
  readonly useInput: string;
  readonly moveInput: string;
  /** FS-12 F20 — a player opening a manual mid-attempt assumes the worst. Say the attempt is live. */
  readonly dailyAttemptLive: boolean;
}

/** Render into `container`. Returns a disposer that removes the Escape listener. */
export function renderHowToPlay(
  container: HTMLElement,
  opts: HowToPlayOptions,
  onBack: () => void,
): () => void {
  const cards = howToPlayCards();
  const section = (s: HowToPlaySection): string =>
    cards
      .filter((c) => c.section === s)
      .map(cardHtml)
      .join('');

  container.innerHTML = `
    <h1 class="title small">HOW TO PLAY</h1>
    <p class="subtitle">R E S O N A N C E</p>
    <p class="pitch">The hazards are the waveform of the music, and every one of them has a gap.
      Find the gap, fly the gap. ${escapeHtml(opts.moveInput)} to move, ${escapeHtml(opts.useInput)} to spend a power-up.</p>
    ${opts.dailyAttemptLive ? '<p class="best-tag" style="color:var(--cyan)">YOUR DAILY ATTEMPT IS STILL LIVE — READING THIS DOES NOT SPEND IT</p>' : ''}

    <h2 class="htp-section">THE HAZARDS</h2>
    <div class="htp-grid">${section('hazards')}</div>

    <h2 class="htp-section">POWER-UPS</h2>
    <div class="htp-grid">${section('powerups')}</div>

    <h2 class="htp-section">THE READOUTS</h2>
    <div class="htp-grid">${section('hud')}</div>

    <div class="hints"><p><a id="btn-htp-back" class="link">back</a> · <b>Esc</b></p></div>
  `;

  // R40 — dismissible with a single input, and it returns focus to whoever opened it.
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      dispose();
      onBack();
    }
  };
  const dispose = (): void => window.removeEventListener('keydown', onKey);
  window.addEventListener('keydown', onKey);

  const back = container.querySelector<HTMLElement>('#btn-htp-back');
  back?.addEventListener('click', () => {
    dispose();
    onBack();
  });
  back?.focus();
  return dispose;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}
